import { Building } from 'lucide-react';

/**
 * Visualisation simple de l'arborescence des services (US-SVC-03).
 *
 * Pas de dépendance externe — rendu récursif avec Tailwind. Chaque noeud est
 * une carte cliquable (ouvre le drawer détail). Les enfants apparaissent en
 * sous-liste indentée avec un connecteur vertical (border-l).
 */

export interface ServiceTreeNode {
  id: string;
  code: string;
  nom_complet: string;
  type_service: string;
  statut: string;
  service_parent_id: string | null;
  enfants: ServiceTreeNode[];
}

export function ServicesOrganigramme({
  tree,
  onNodeClick,
}: {
  tree: ServiceTreeNode[];
  onNodeClick(id: string): void;
}) {
  if (tree.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic p-6 text-center">Aucun service à afficher.</p>
    );
  }

  return (
    <div className="space-y-3">
      {tree.map((root) => (
        <Node key={root.id} node={root} onNodeClick={onNodeClick} depth={0} />
      ))}
    </div>
  );
}

function Node({
  node,
  onNodeClick,
  depth,
}: {
  node: ServiceTreeNode;
  onNodeClick(id: string): void;
  depth: number;
}) {
  const statutCfg = STATUT_BADGE[node.statut] ?? STATUT_BADGE.default;
  return (
    <div>
      <button
        type="button"
        onClick={() => onNodeClick(node.id)}
        className="w-full text-left rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/40 bg-white px-3 py-2 transition-colors"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1 flex items-center">
            <Building className="w-4 h-4 text-primary-600 mr-2 flex-shrink-0" />
            <span className="text-xs font-semibold text-primary-700 uppercase tracking-wide mr-2">
              {node.code}
            </span>
            <span className="text-sm text-gray-900 truncate">{node.nom_complet}</span>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-blue-100 text-blue-800 border-blue-200 capitalize">
              {node.type_service.replace(/_/g, ' ')}
            </span>
            <span
              className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${statutCfg}`}
            >
              {node.statut.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </button>

      {node.enfants.length > 0 && (
        <ul className="mt-2 ml-4 md:ml-6 space-y-2 border-l border-gray-200 pl-3 md:pl-4">
          {node.enfants.map((child) => (
            <li key={child.id}>
              <Node node={child} onNodeClick={onNodeClick} depth={depth + 1} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const STATUT_BADGE: Record<string, string> = {
  actif: 'bg-green-100 text-green-800 border-green-200',
  inactif: 'bg-gray-100 text-gray-700 border-gray-200',
  en_restructuration: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  ferme_temporairement: 'bg-orange-100 text-orange-800 border-orange-200',
  desaffecte: 'bg-red-100 text-red-800 border-red-200',
  default: 'bg-gray-100 text-gray-700 border-gray-200',
};
